import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useParams } from 'react-router';

import {
    useGetDocumentAuditsQuery,
    useGetInvestmentFundsQuery,
    useValidateInscriptionMutation,
} from 'api/main';
import { distributorType, docTypes, RoleNames, validationStatus } from 'utils/constants';
import { useDistributorDocuments, useMatchAtLeastOneRole } from 'utils/hooks';
import { documentSorter, jsonParseSafe } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import InfoBox from './InfoBox';
import Document from 'containers/subscriptions/Document';
import UploadDocument from 'containers/subscriptions/UploadDocument';
import { Comments, CustomButton, CustomSelect, Loader } from 'components';
import { Box, MenuItem, Typography } from '@mui/material';

const DocumentsSection = props => {
    const { canValidateDocuments, canAddDocument, disableNewComment, distributor, refetch } = props;
    const { id } = useParams();
    const [selectedFund, setSelectedFund] = useState('');

    const [validateInscription, { isLoading: validateCompanyLoader }] =
        useValidateInscriptionMutation();
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });

    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const userIsAdminOrSignatoryServicer = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);

    const subscription = useMemo(() => distributor?.user?.subscriptions?.[0], [distributor]);

    const { documents, loading } = useDistributorDocuments(distributor, distributor?.user?.id);
    const {
        data: documentAudits = [],
        isFetching: documentAuditsLoader,
        refetch: refetchDocumentAudit,
    } = useGetDocumentAuditsQuery(
        { belongsToId: distributor.user.id, pagination: false },
        { skip: !distributor?.user?.id }
    );

    const allDistributorDocumentsValidated = useMemo(
        () =>
            documents.every(
                doc =>
                    doc.validationStatus !== validationStatus.NOT_VALIDATED ||
                    (doc.type === docTypes.OTHER ? doc.createdBy?.id !== doc.belongsTo?.id : false)
            ),
        [documents]
    );
    const invoices = useMemo(
        () =>
            documents.filter(
                doc =>
                    doc.type === docTypes.DISTRIBUTOR_BILL ||
                    doc.type === docTypes.DISTRIBUTOR_BILL_EXCEL
            ),
        [documents]
    );
    const inscriptionDocuments = useMemo(
        () =>
            documents.filter(
                doc =>
                    doc.type !== docTypes.DISTRIBUTOR_BILL &&
                    doc.type !== docTypes.DISTRIBUTOR_BILL_EXCEL
            ),
        [documents]
    );
    const fundsFromInvoices = useMemo(() => {
        const set = new Set();
        invoices.forEach(inv => set.add(+jsonParseSafe(inv.subtype)?.investmentFund));
        return Array.from(set);
    }, [invoices]);
    const filteredFunds = useMemo(
        () => fundList.filter(f => fundsFromInvoices.includes(f.id)),
        [fundsFromInvoices, fundList]
    );

    const handleDocumentsChange = useCallback(
        callback => {
            callback?.();
            refetch();
            refetchDocumentAudit();
        },
        [refetch, refetchDocumentAudit]
    );

    const handleInscriptionValidation = useCallback(() => {
        validateInscription(id);
    }, [validateInscription, id]);

    useEffect(() => {
        setSelectedFund(filteredFunds?.[0]?.id || '');
    }, [filteredFunds]);

    return (
        <InfoBox title={texts.DOCUMENTS} disableLeftColumn={!documents.length}>
            <Loader loading={loading || fundsLoader || documentAuditsLoader} flex="1 1">
                <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                    {texts.DISTRIBUTOR_DOCUMENTS}
                </Typography>
                {inscriptionDocuments.map(doc => {
                    const canArchive =
                        canAddDocument &&
                        distributor.documentsValidated &&
                        doc.validationStatus !== validationStatus.NOT_VALIDATED &&
                        doc.type !== docTypes.OTHER;

                    return (
                        <Document
                            key={doc.id}
                            doc={doc}
                            displayValidation={canAddDocument && doc.type !== docTypes.OTHER}
                            canArchive={canArchive}
                            canValidate={canValidateDocuments}
                            onChange={handleDocumentsChange}
                        />
                    );
                })}
                {!!invoices.length && (
                    <React.Fragment>
                        <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                            {texts.INVOICES}
                        </Typography>
                        <CustomSelect
                            value={selectedFund}
                            onChange={e => setSelectedFund(+e.target.value)}
                        >
                            {filteredFunds.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        {invoices
                            .filter(
                                inv => +jsonParseSafe(inv.subtype).investmentFund === +selectedFund
                            )
                            .sort(documentSorter)
                            .map(doc => (
                                <Document
                                    key={doc.id}
                                    doc={doc}
                                    fullName={doc.name.replace(/\.[^.]+$/, '')}
                                    canValidate={canValidateDocuments}
                                    onChange={handleDocumentsChange}
                                />
                            ))}
                    </React.Fragment>
                )}
                {(userIsAdminOrSignatoryServicer || userIsStandardServicer) &&
                    !!documentAudits.length && (
                        <Box mb={3}>
                            <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
                                {texts.OLD_DOCUMENTS}
                            </Typography>
                            {documentAudits.map(doc => (
                                <Document key={doc.id} doc={doc} isReplaceable={false} isAudit />
                            ))}
                        </Box>
                    )}
                {!documents.length && <Typography align="center">{texts.NO_DOCUMENTS}</Typography>}
                {canAddDocument && (
                    <UploadDocument userId={distributor?.user?.id} uploadByAdmin hideSelect />
                )}
                {canValidateDocuments &&
                    !distributor?.documentsValidated &&
                    distributor?.type === distributorType.STANDARD &&
                    (distributor?.promoters?.length ? (
                        <CustomButton
                            noMargin
                            sx={{ marginTop: '30px' }}
                            id={`validate-account-button`}
                            label={texts.INSCRIPTION_VALIDATION}
                            onClick={handleInscriptionValidation}
                            disabled={
                                distributor?.documentsValidated ||
                                !allDistributorDocumentsValidated ||
                                validateCompanyLoader
                            }
                            loading={validateCompanyLoader}
                        />
                    ) : (
                        <Typography fontSize={16} fontStyle="italic" color="grey.600" mt="30px">
                            {texts.AT_LEAST_ONE_PROMOTER_FOR_VALIDATION}
                        </Typography>
                    ))}
                <Comments
                    subscription={subscription}
                    disableLeftPadding
                    disableNewComment={disableNewComment}
                />
            </Loader>
        </InfoBox>
    );
};

DocumentsSection.propTypes = {
    distributor: PropTypes.object,
    disableNewComment: PropTypes.bool,
    canValidateDocuments: PropTypes.bool,
    canAddDocument: PropTypes.bool,
    refetch: PropTypes.func.isRequired,
};

export default DocumentsSection;
